import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInAnonymously,
  GoogleAuthProvider,
  GithubAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  User,
  onAuthStateChanged,
  Unsubscribe
} from 'firebase/auth';
import { auth, db, retryFirestoreOperation, isOnline } from './config';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';

// Auth providers
const googleProvider = new GoogleAuthProvider();
const githubProvider = new GithubAuthProvider();

// User profile interface
export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  createdAt: Date;
  updatedAt: Date;
  subscription: 'free' | 'pro' | 'enterprise';
  apiKeys?: {
    openai?: string;
    anthropic?: string;
    gemini?: string;
  };
  preferences?: {
    theme: 'light' | 'dark';
    defaultPlatform: string;
    autoSave: boolean;
  };
  usage?: {
    promptsGenerated: number;
    promptsSaved: number;
    apiCalls: number;
    lastActive: Date;
  };
}

// Create or update user profile in Firestore
const createUserProfile = async (user: User): Promise<UserProfile> => {
  // Check if we're online first
  if (!isOnline()) {
    console.warn('Network is offline, deferring profile creation');
    // Return a temporary profile object
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      createdAt: new Date(),
      updatedAt: new Date(),
      subscription: 'free',
      preferences: {
        theme: 'light',
        defaultPlatform: 'midjourney',
        autoSave: true
      },
      usage: {
        promptsGenerated: 0,
        promptsSaved: 0,
        apiCalls: 0,
        lastActive: new Date()
      }
    };
  }

  return retryFirestoreOperation(async () => {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
    // Create new user profile
    const newProfile: UserProfile = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      createdAt: new Date(),
      updatedAt: new Date(),
      subscription: 'free',
      preferences: {
        theme: 'light',
        defaultPlatform: 'midjourney',
        autoSave: true
      },
      usage: {
        promptsGenerated: 0,
        promptsSaved: 0,
        apiCalls: 0,
        lastActive: new Date()
      }
    };

      await setDoc(userRef, newProfile);
      return newProfile;
    } else {
      // Update last active
      await updateDoc(userRef, {
        'usage.lastActive': new Date(),
        updatedAt: new Date()
      });
      return userSnap.data() as UserProfile;
    }
  });
};

// Authentication functions
export const authService = {
  // Sign up with email and password
  signUp: async (email: string, password: string, displayName?: string) => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      if (displayName) {
        await updateProfile(user, { displayName });
      }
      
      const profile = await createUserProfile(user);
      return { user, profile };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sign up');
    }
  },

  // Sign in with email and password
  signIn: async (email: string, password: string) => {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      const profile = await createUserProfile(user);
      return { user, profile };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sign in');
    }
  },

  // Sign in with Google
  signInWithGoogle: async () => {
    try {
      const { user } = await signInWithPopup(auth, googleProvider);
      const profile = await createUserProfile(user);
      return { user, profile };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  },

  // Sign in with GitHub
  signInWithGitHub: async () => {
    try {
      const { user } = await signInWithPopup(auth, githubProvider);
      const profile = await createUserProfile(user);
      return { user, profile };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sign in with GitHub');
    }
  },

  // Sign in anonymously (for guest access)
  signInAnonymously: async () => {
    try {
      const { user } = await signInAnonymously(auth);
      const profile = await createUserProfile(user);
      return { user, profile };
    } catch (error: any) {
      // If anonymous auth fails, provide a fallback
      console.error('Anonymous sign-in failed:', error);
      throw new Error(error.message || 'Failed to sign in anonymously');
    }
  },

  // Sign out
  signOut: async () => {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sign out');
    }
  },

  // Reset password
  resetPassword: async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to send reset email');
    }
  },

  // Update profile
  updateProfile: async (updates: { displayName?: string; photoURL?: string }) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No user logged in');

      await updateProfile(user, updates);
      
      // Update Firestore profile
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: new Date()
      });

      return user;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update profile');
    }
  },

  // Update password
  updatePassword: async (currentPassword: string, newPassword: string) => {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) throw new Error('No user logged in');

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, newPassword);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update password');
    }
  },

  // Get current user profile
  getCurrentUserProfile: async (): Promise<UserProfile | null> => {
    try {
      const user = auth.currentUser;
      if (!user) return null;

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return userSnap.data() as UserProfile;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  },

  // Subscribe to auth state changes
  onAuthStateChange: (callback: (user: User | null, profile: UserProfile | null) => void): Unsubscribe => {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const profile = await createUserProfile(user);
          callback(user, profile);
        } catch (error) {
          console.error('Error in auth state change:', error);
          // Still provide the user even if profile fails
          callback(user, null);
        }
      } else {
        // Auto sign-in anonymously if no user
        if (isOnline()) {
          try {
            const { user: anonUser } = await signInAnonymously(auth);
            const profile = await createUserProfile(anonUser);
            callback(anonUser, profile);
          } catch (error) {
            console.error('Failed to sign in anonymously:', error);
            callback(null, null);
          }
        } else {
          callback(null, null);
        }
      }
    });
  },

  // Update user preferences
  updateUserPreferences: async (preferences: Partial<UserProfile['preferences']>) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No user logged in');

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        preferences,
        updatedAt: new Date()
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update preferences');
    }
  },

  // Update API keys (encrypted in production)
  updateApiKeys: async (apiKeys: Partial<UserProfile['apiKeys']>) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No user logged in');

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        apiKeys,
        updatedAt: new Date()
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update API keys');
    }
  },

  // Track usage
  trackUsage: async (type: 'promptsGenerated' | 'promptsSaved' | 'apiCalls', increment: number = 1) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const currentUsage = userSnap.data().usage || {};
        await updateDoc(userRef, {
          [`usage.${type}`]: (currentUsage[type] || 0) + increment,
          'usage.lastActive': new Date(),
          updatedAt: new Date()
        });
      }
    } catch (error) {
      console.error('Error tracking usage:', error);
    }
  }
};

export default authService;